import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import scheduleService from '../../services/scheduleService';
import { getAgeGroupColors } from '../../utils/ageGroupColors';
import ConfirmationModal from './ConfirmationModal';
import Toast from './Toast';

const ScheduleBlockPreview = () => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('blocks');
    const [expandedAgeGroups, setExpandedAgeGroups] = useState({});

    // Edit Mode State
    const [isEditMode, setIsEditMode] = useState(false);
    const [editBlocks, setEditBlocks] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [toast, setToast] = useState({ show: false, message: "", type: "info" });
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

    // Drag State
    const [dragItem, setDragItem] = useState(null);
    const [dragOverTarget, setDragOverTarget] = useState(null);

    const toggleAgeGroup = (id) => {
        setExpandedAgeGroups(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const sortedAgeGroupList = useMemo(() => {
        if (!data?.ageGroupData) return [];
        return Object.values(data.ageGroupData).sort((a, b) => {
            const da = a.minAge ?? 0;
            const db = b.minAge ?? 0;
            if (da !== db) return da - db;
            return String(a.ageGroupName || '').localeCompare(String(b.ageGroupName || ''), 'de');
        });
    }, [data]);

    const expandAllSortingSections = useCallback(() => {
        if (!data?.ageGroupData) return;
        const next = {};
        Object.keys(data.ageGroupData).forEach((id) => {
            next[id] = true;
        });
        setExpandedAgeGroups(next);
    }, [data]);

    const collapseAllSortingSections = useCallback(() => {
        setExpandedAgeGroups({});
    }, []);

    const partnerFormsSummary = useMemo(() => {
        const list = data?.partnerForms;
        if (!list?.length) return { count: 0, totalTn: 0, totalPairs: 0 };
        let totalTn = 0;
        let totalPairs = 0;
        list.forEach((p) => {
            const tn = p.participantCount || 0;
            totalTn += tn;
            totalPairs += p.pairCount ?? Math.ceil(tn / 2);
        });
        return { count: list.length, totalTn, totalPairs };
    }, [data?.partnerForms]);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await scheduleService.getBlockPreview();
            if (result.success) {
                setData(result.data);
                // Erste zwei Altersgruppen standardmäßig öffnen
                if (result.data.ageGroupData) {
                    const ids = Object.keys(result.data.ageGroupData);
                    const initial = {};
                    ids.slice(0, 2).forEach(id => { initial[id] = true; });
                    setExpandedAgeGroups(prev => ({ ...initial, ...prev }));
                }
            } else {
                setError(result.error || 'Fehler beim Laden');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const addToast = useCallback((type, message) => {
        setToast({ show: true, message, type });
    }, []);

    const hideToast = useCallback(() => {
        setToast({ show: false, message: "", type: "info" });
    }, []);

    // === Edit Mode Handlers ===
    const handleStartEdit = useCallback(() => {
        if (!data?.blocks) return;
        setEditBlocks(JSON.parse(JSON.stringify(data.blocks)));
        setIsEditMode(true);
        setHasChanges(false);
    }, [data]);

    const handleDiscardEdit = useCallback(() => {
        if (hasChanges) {
            setShowDiscardConfirm(true);
            return;
        }
        setIsEditMode(false);
        setEditBlocks(null);
        setHasChanges(false);
    }, [hasChanges]);

    const handleConfirmDiscard = useCallback(() => {
        setShowDiscardConfirm(false);
        setIsEditMode(false);
        setEditBlocks(null);
        setHasChanges(false);
        addToast('info', 'Änderungen verworfen');
    }, [addToast]);

    const handleSaveEdit = useCallback(async () => {
        if (!editBlocks || isSaving) return;
        setIsSaving(true);
        try {
            const result = await scheduleService.saveBlockOrder(editBlocks);
            if (result.success) {
                addToast('success', 'Block-Reihenfolge gespeichert');
                setIsEditMode(false);
                setEditBlocks(null);
                setHasChanges(false);
                await loadData();
            } else {
                addToast('error', result.error || 'Fehler beim Speichern');
            }
        } catch (err) {
            addToast('error', 'Fehler beim Speichern');
        } finally {
            setIsSaving(false);
        }
    }, [editBlocks, isSaving, addToast]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            const tag = document.activeElement?.tagName?.toLowerCase();
            if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

            if (e.key === 'e' && !e.ctrlKey && !e.metaKey && !e.altKey && activeTab === 'blocks') {
                e.preventDefault();
                if (!isEditMode) handleStartEdit();
                else handleDiscardEdit();
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 's' && isEditMode) {
                e.preventDefault();
                if (hasChanges && !isSaving) handleSaveEdit();
                return;
            }
            if (e.key === 'Escape') {
                if (showDiscardConfirm) setShowDiscardConfirm(false);
                else if (isEditMode) handleDiscardEdit();
                return;
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isEditMode, hasChanges, isSaving, activeTab, showDiscardConfirm, handleStartEdit, handleDiscardEdit, handleSaveEdit]);

    // === Drag & Drop Validation ===
    const canDropPair = useCallback((dragBlockIdx, dragSlotIdx, targetBlockIdx, targetSlotIdx) => {
        if (!editBlocks) return false;
        if (dragBlockIdx === targetBlockIdx && dragSlotIdx === targetSlotIdx) return false;
        const dragSlot = editBlocks[dragBlockIdx].slots[dragSlotIdx];
        const targetSlot = editBlocks[targetBlockIdx].slots[targetSlotIdx];
        // Only allow drop on a slot with the SAME ageGroup
        return targetSlot.ageGroup === dragSlot.ageGroup;
    }, [editBlocks]);

    // === Pair Drag Handlers ===
    const handlePairDragStart = useCallback((e, blockIdx, slotIdx) => {
        setDragItem({ type: 'pair', blockIdx, slotIdx });
        e.dataTransfer.effectAllowed = 'move';
        if (e.target) e.dataTransfer.setDragImage(e.target, 0, 0);
    }, []);

    const handlePairDragOver = useCallback((e, blockIdx, slotIdx) => {
        e.preventDefault();
        if (!dragItem || dragItem.type !== 'pair') return;
        const canDrop = canDropPair(dragItem.blockIdx, dragItem.slotIdx, blockIdx, slotIdx);
        e.dataTransfer.dropEffect = canDrop ? 'move' : 'none';
        setDragOverTarget({ type: 'pair', blockIdx, slotIdx, valid: canDrop });
    }, [dragItem, canDropPair]);

    const handlePairDrop = useCallback((e, targetBlockIdx, targetSlotIdx) => {
        e.preventDefault();
        if (!dragItem || dragItem.type !== 'pair' || !editBlocks) return;
        if (!canDropPair(dragItem.blockIdx, dragItem.slotIdx, targetBlockIdx, targetSlotIdx)) return;

        const newBlocks = JSON.parse(JSON.stringify(editBlocks));
        const sourceSlot = newBlocks[dragItem.blockIdx].slots[dragItem.slotIdx];
        const targetSlot = newBlocks[targetBlockIdx].slots[targetSlotIdx];

        // Swap male + female entries between source and target
        const tempMale = sourceSlot.male;
        const tempFemale = sourceSlot.female;
        sourceSlot.male = targetSlot.male;
        sourceSlot.female = targetSlot.female;
        targetSlot.male = tempMale;
        targetSlot.female = tempFemale;

        // Recalculate block durations
        recalcBlockDurations(newBlocks);

        setEditBlocks(newBlocks);
        setHasChanges(true);
        setDragItem(null);
        setDragOverTarget(null);
    }, [dragItem, editBlocks, canDropPair]);

    // === Block Drag Handlers ===
    const handleBlockDragStart = useCallback((e, blockIdx) => {
        setDragItem({ type: 'block', blockIdx });
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    const handleBlockDragOver = useCallback((e, blockIdx) => {
        e.preventDefault();
        if (!dragItem || dragItem.type !== 'block') return;
        if (dragItem.blockIdx === blockIdx) return;
        e.dataTransfer.dropEffect = 'move';
        setDragOverTarget({ type: 'block', blockIdx, valid: true });
    }, [dragItem]);

    const handleBlockDrop = useCallback((e, targetBlockIdx) => {
        e.preventDefault();
        if (!dragItem || dragItem.type !== 'block' || !editBlocks) return;
        if (dragItem.blockIdx === targetBlockIdx) return;

        const newBlocks = JSON.parse(JSON.stringify(editBlocks));
        // Swap the slots arrays of both blocks
        const tempSlots = newBlocks[dragItem.blockIdx].slots;
        newBlocks[dragItem.blockIdx].slots = newBlocks[targetBlockIdx].slots;
        newBlocks[targetBlockIdx].slots = tempSlots;

        recalcBlockDurations(newBlocks);

        setEditBlocks(newBlocks);
        setHasChanges(true);
        setDragItem(null);
        setDragOverTarget(null);
    }, [dragItem, editBlocks]);

    const handleDragEnd = useCallback(() => {
        setDragItem(null);
        setDragOverTarget(null);
    }, []);

    // Recalculate block durations after a swap
    const recalcBlockDurations = (blocks) => {
        blocks.forEach(block => {
            let dA = 0, dB = 0;
            block.slots.forEach(slot => {
                if (slot.male) {
                    if (slot.maleVenue === 'Fläche A') dA += slot.male.duration;
                    else dB += slot.male.duration;
                }
                if (slot.female) {
                    if (slot.femaleVenue === 'Fläche A') dA += slot.female.duration;
                    else dB += slot.female.duration;
                }
            });
            block.totalDurationA = Math.round(dA * 10) / 10;
            block.totalDurationB = Math.round(dB * 10) / 10;
        });
    };

    /** Geschlecht in Klammern: Icon + Buchstabe, farbig — ohne Badge/Pill */
    const GenderInline = ({ gender, small }) => {
        const color =
            gender === 'männlich'
                ? 'text-sky-600 dark:text-sky-400'
                : gender === 'weiblich'
                    ? 'text-pink-600 dark:text-pink-400'
                    : 'text-violet-600 dark:text-violet-400';
        const icon = gender === 'männlich' ? 'fa-mars' : gender === 'weiblich' ? 'fa-venus' : 'fa-venus-mars';
        const letter = gender === 'männlich' ? 'M' : gender === 'weiblich' ? 'W' : 'M/W';
        const size = small ? 'text-[10px]' : 'text-xs';
        const iconSize = small ? 'text-[9px]' : 'text-[10px]';
        return (
            <span className={`inline-flex items-center gap-0.5 font-semibold tabular-nums ${size} ${color}`} title={gender === 'gemischt' ? 'Gemischt' : gender === 'männlich' ? 'Männlich' : 'Weiblich'}>
                <span className="font-medium opacity-90">(</span>
                <i className={`fas ${icon} ${iconSize} opacity-95`} aria-hidden="true"></i>
                <span>{letter}</span>
                <span className="font-medium opacity-90">)</span>
            </span>
        );
    };

    // Duration Badge
    const DurationBadge = ({ minutes }) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
            <i className="fas fa-clock text-[9px] mr-1 opacity-60" aria-hidden="true"></i>
            {minutes} min
        </span>
    );

    const deltaPairingClass = (diff) => {
        const d = Number(diff);
        if (Number.isNaN(d) || d < 0) return 'bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600';
        if (d <= 3) return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
        if (d <= 8) return 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800';
        return 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800';
    };

    const venueSurfaceBadgeClass = (venue) => {
        if (venue === 'Fläche A') {
            return 'border-sky-200/90 dark:border-sky-800/70 bg-sky-50 dark:bg-sky-950/30 text-sky-800 dark:text-sky-200';
        }
        if (venue === 'Fläche B') {
            return 'border-violet-200/90 dark:border-violet-800/70 bg-violet-50 dark:bg-violet-950/30 text-violet-800 dark:text-violet-200';
        }
        return 'border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200';
    };

    const renderPairingSideCell = (entry) => {
        if (!entry) {
            return (
                <div className="flex min-h-[3.25rem] items-center gap-2 text-xs text-slate-400 dark:text-slate-500">
                    <i className="fas fa-minus-circle text-[10px] opacity-45 shrink-0" aria-hidden="true"></i>
                    <span className="italic">Kein Eintrag</span>
                </div>
            );
        }
        return (
            <div className="min-w-0 pt-0.5">
                <p className="font-semibold text-sm text-slate-800 dark:text-slate-100 leading-snug break-words">
                    {entry.categoryName || entry.name}
                </p>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
                    <span className="inline-flex items-center gap-1.5">
                        <i className="fas fa-users text-[9px] w-3.5 text-center opacity-70" aria-hidden="true"></i>
                        <span>{entry.participants} TN</span>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <i className="fas fa-clock text-[9px] w-3.5 text-center opacity-70" aria-hidden="true"></i>
                        <span>{entry.duration} min</span>
                    </span>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mb-8 animate-pulse">
                        <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-64 mb-2"></div>
                        <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-96"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6 animate-pulse">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
                                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24 mb-3"></div>
                                <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-16"></div>
                            </div>
                        ))}
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 animate-pulse">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="h-40 bg-slate-200 dark:bg-slate-700 rounded-xl mb-4"></div>
                        ))}
                    </div>
                </div>
            </main>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-slate-50/50 dark:bg-slate-900 transition-colors">
                <main className="pt-24 pb-8">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-center min-h-[50vh]">
                            <div className="text-center max-w-md bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm px-8 py-10">
                                <div className="inline-flex items-center justify-center w-14 h-14 bg-red-50 dark:bg-red-900/25 rounded-2xl mb-5 border border-red-100 dark:border-red-900/40">
                                    <i className="fas fa-exclamation-triangle text-lg text-red-600 dark:text-red-400" aria-hidden="true"></i>
                                </div>
                                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1">Daten konnten nicht geladen werden</p>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">{error}</p>
                                <button
                                    type="button"
                                    onClick={loadData}
                                    className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-medium shadow-sm transition-colors"
                                >
                                    <i className="fas fa-sync-alt text-xs" aria-hidden="true"></i>
                                    Erneut versuchen
                                </button>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    if (!data) return null;

    const tabs = [
        { id: 'blocks', label: 'Blöcke', icon: 'fa-cubes' },
        { id: 'sorting', label: 'Dauer-Sortierung', icon: 'fa-sort-amount-down' },
        { id: 'pairings', label: 'Pairings', icon: 'fa-link' },
        { id: 'partners', label: 'Partnerformen', icon: 'fa-handshake' },
        { id: 'overview', label: 'Flächen-Zuweisung', icon: 'fa-map-marker-alt' },
    ];

    // Group pairings by block
    const blockPairings = {};
    if (data.pairings) {
        data.pairings.forEach(p => {
            if (!blockPairings[p.block]) blockPairings[p.block] = [];
            blockPairings[p.block].push(p);
        });
    }

    // Determine which blocks to render
    const displayBlocks = isEditMode && editBlocks ? editBlocks : (data?.blocks || []);

    return (
        <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                <Toast
                    message={toast.message}
                    type={toast.type}
                    isVisible={toast.show}
                    onClose={hideToast}
                />

                {/* Discard Confirmation Modal (ConfirmationModal component) */}
                <ConfirmationModal
                    isOpen={showDiscardConfirm}
                    onClose={() => setShowDiscardConfirm(false)}
                    onConfirm={handleConfirmDiscard}
                    title="Änderungen verwerfen?"
                    subtitle="Diese Aktion kann nicht rückgängig gemacht werden."
                    message="Alle ungespeicherten Änderungen an der Block-Reihenfolge gehen verloren."
                    confirmText="Verwerfen"
                    cancelText="Abbrechen"
                    type="warning"
                    layout="inline"
                />

                {/* Edit Mode Floating Toolbar */}
                {isEditMode && (
                    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 w-[calc(100vw-1.5rem)] max-w-2xl px-1">
                        <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 rounded-2xl border border-slate-200/90 dark:border-slate-600/80 bg-white/95 dark:bg-slate-800/95 backdrop-blur-md shadow-lg px-4 py-3 sm:px-5">
                            <div className="flex items-center gap-2 min-w-0">
                                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0" aria-hidden="true"></span>
                                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">Bearbeitungsmodus</span>
                                {hasChanges && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border border-amber-200/80 dark:border-amber-800/50 shrink-0">
                                        <i className="fas fa-circle text-[5px] mr-1 text-amber-500" aria-hidden="true"></i>
                                        Ungespeichert
                                    </span>
                                )}
                            </div>
                            <div className="hidden sm:block w-px h-6 bg-slate-200 dark:bg-slate-600 shrink-0" aria-hidden="true"></div>
                            <div className="flex items-center gap-2 flex-wrap justify-center">
                                <button
                                    type="button"
                                    onClick={handleSaveEdit}
                                    disabled={!hasChanges || isSaving}
                                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                                        hasChanges && !isSaving
                                            ? 'bg-primary dark:bg-primary-light text-white hover:bg-primary-dark dark:hover:bg-primary shadow-sm'
                                            : 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                                    }`}
                                >
                                    <i className={`fas ${isSaving ? 'fa-spinner fa-spin' : 'fa-save'} text-xs`} aria-hidden="true"></i>
                                    {isSaving ? 'Speichern…' : 'Speichern'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleDiscardEdit}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                                >
                                    <i className="fas fa-times text-xs" aria-hidden="true"></i>
                                    Abbrechen
                                </button>
                            </div>
                            <div className="hidden md:flex items-center gap-2 text-[10px] text-slate-400 dark:text-slate-500 w-full sm:w-auto justify-center">
                                <kbd className="px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-700/80 font-mono border border-slate-200/80 dark:border-slate-600">Ctrl+S</kbd>
                                <span>Speichern</span>
                                <kbd className="px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-700/80 font-mono border border-slate-200/80 dark:border-slate-600">Esc</kbd>
                                <span>Abbrechen</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                <button
                                    type="button"
                                    onClick={() => navigate('/admin/schedule')}
                                    className="inline-flex items-center justify-center w-9 h-9 rounded-xl text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-white dark:hover:bg-slate-800 border border-transparent hover:border-slate-200 dark:hover:border-slate-600 transition-colors shrink-0"
                                    aria-label="Zurück zum Zeitplan"
                                >
                                    <i className="fas fa-arrow-left text-sm" aria-hidden="true"></i>
                                </button>
                                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                                    Block-Rotation Vorschau
                                </h1>
                                <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-lg text-[10px] font-semibold uppercase tracking-widest bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200/80 dark:border-slate-600/80">
                                    Admin
                                </span>
                            </div>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1.5 ml-0 sm:ml-12 max-w-2xl leading-relaxed">
                                Übersicht und Debug für die automatische Zeitplanerstellung — Blöcke, Pairings und Flächenlogik auf einen Blick.
                            </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 sm:pt-1">
                            {activeTab === 'blocks' && !isEditMode && (
                                <button
                                    type="button"
                                    onClick={handleStartEdit}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors border bg-primary dark:bg-primary-light text-white border-primary dark:border-primary-light hover:bg-primary-dark dark:hover:bg-primary shadow-sm"
                                >
                                    <i className="fas fa-edit text-xs" aria-hidden="true"></i>
                                    Bearbeiten
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={loadData}
                                disabled={isEditMode}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors border ${
                                    isEditMode
                                        ? 'bg-slate-50 dark:bg-slate-800 text-slate-400 dark:text-slate-500 border-slate-200 dark:border-slate-700 cursor-not-allowed'
                                        : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:text-primary dark:hover:text-primary-light hover:border-primary/30 dark:hover:border-primary-light/30'
                                }`}
                            >
                                <i className="fas fa-sync-alt text-xs" aria-hidden="true"></i>
                                Aktualisieren
                            </button>
                        </div>
                    </div>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    {[
                        { label: 'Gruppen gesamt', value: data.summary.totalGroups, icon: 'fa-layer-group', color: 'text-blue-600 dark:text-blue-400', bgIcon: 'bg-blue-100 dark:bg-blue-900/30' },
                        { label: 'Reguläre Gruppen', value: data.summary.regularGroups, icon: 'fa-user', color: 'text-emerald-600 dark:text-emerald-400', bgIcon: 'bg-emerald-100 dark:bg-emerald-900/30' },
                        { label: 'Partnerformen', value: data.summary.partnerGroups, icon: 'fa-handshake', color: 'text-purple-600 dark:text-purple-400', bgIcon: 'bg-purple-100 dark:bg-purple-900/30' },
                        { label: 'Blöcke', value: data.summary.totalBlocks, icon: 'fa-cubes', color: 'text-amber-600 dark:text-amber-400', bgIcon: 'bg-amber-100 dark:bg-amber-900/30' },
                    ].map(stat => (
                        <div key={stat.label} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1">{stat.label}</p>
                                    <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{stat.value}</p>
                                </div>
                                <div className={`w-10 h-10 rounded-full ${stat.bgIcon} flex items-center justify-center`}>
                                    <i className={`fas ${stat.icon} ${stat.color} text-sm`}></i>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Einstellungen (Kontext) */}
                <div className="mb-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm px-4 py-3 sm:px-5">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2.5">Zeitplan-Parameter</p>
                    <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-medium text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-700/40 border border-slate-200/80 dark:border-slate-600/60">
                            <i className="fas fa-stopwatch text-slate-400 dark:text-slate-500 text-[11px]" aria-hidden="true"></i>
                            {data.settings.minutesPerAthlete} min je Athlet
                        </span>
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-medium text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-700/40 border border-slate-200/80 dark:border-slate-600/60">
                            <i className="fas fa-pause text-slate-400 dark:text-slate-500 text-[11px]" aria-hidden="true"></i>
                            {data.settings.bufferMinutes} min Puffer
                        </span>
                    </div>
                </div>

                {/* Tabs (segmentiert) */}
                <div className="mb-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-100/80 dark:bg-slate-800/80 p-1 shadow-inner">
                    <div className="flex gap-0.5 overflow-x-auto pb-0.5 -mx-0.5 px-0.5 sm:mx-0 sm:px-0">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all shrink-0 ${activeTab === tab.id
                                    ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm ring-1 ring-slate-200/80 dark:ring-slate-600'
                                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-white/60 dark:hover:bg-slate-700/50'
                                    }`}
                            >
                                <i className={`fas ${tab.icon} text-[11px] opacity-80`} aria-hidden="true"></i>
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content */}
                {activeTab === 'blocks' && (
                    <>
                        <div className="mb-5 rounded-2xl border border-slate-200/90 dark:border-slate-600/80 bg-white dark:bg-slate-800 px-4 py-4 sm:px-5 shadow-sm">
                            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1">
                                Blöcke
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl">
                                Jeder Block fasst <strong className="font-semibold text-slate-800 dark:text-slate-100">Paare</strong> je Altersgruppe mit Zuordnung zu{' '}
                                <strong className="font-semibold text-sky-700 dark:text-sky-300">Fläche A</strong> und{' '}
                                <strong className="font-semibold text-violet-700 dark:text-violet-300">Fläche B</strong>.{' '}
                                <span className="text-slate-500 dark:text-slate-400">
                                    A-/B-Zeiten und Delta entsprechen der Summe der Kategoriedauern auf der jeweiligen Fläche (wie im Zeitplan).
                                </span>
                            </p>
                        </div>

                        {/* Edit Mode Hint */}
                        {isEditMode && (
                            <div className="mb-5 flex items-start gap-3 px-4 py-3.5 rounded-2xl border border-primary/20 dark:border-primary-light/25 bg-primary/[0.06] dark:bg-primary-light/[0.08] text-sm text-slate-700 dark:text-slate-200">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-800/80 text-primary dark:text-primary-light border border-primary/15 dark:border-primary-light/20 shadow-sm">
                                    <i className="fas fa-hand-pointer text-xs" aria-hidden="true"></i>
                                </span>
                                <span className="leading-relaxed pt-0.5">
                                    <strong className="text-slate-900 dark:text-slate-100">Paare tauschen:</strong> Paar auf ein anderes Paar derselben Altersgruppe ziehen.
                                    <span className="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
                                    <strong className="text-slate-900 dark:text-slate-100">Blöcke tauschen:</strong> Block-Header auf einen anderen Block ziehen.
                                    <span className="block mt-1.5 text-xs text-slate-500 dark:text-slate-400">Tastatur: <kbd className="px-1 rounded bg-white/90 dark:bg-slate-800 font-mono text-[10px]">E</kbd> Bearbeiten · <kbd className="px-1 rounded bg-white/90 dark:bg-slate-800 font-mono text-[10px]">Ctrl+S</kbd> Speichern</span>
                                </span>
                            </div>
                        )}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                            {displayBlocks.map((block, blockArrayIdx) => {
                                const delta = Math.abs(block.totalDurationA - block.totalDurationB);
                                const isBlockDragOver = dragOverTarget?.type === 'block' && dragOverTarget.blockIdx === blockArrayIdx;

                                return (
                                    <div
                                        key={block.blockIndex}
                                        className={`bg-white dark:bg-slate-800 rounded-2xl border shadow-sm overflow-hidden transition-all duration-150 ${
                                            isBlockDragOver && dragOverTarget.valid
                                                ? 'border-primary dark:border-primary-light ring-2 ring-primary/20 dark:ring-primary-light/20 scale-[1.01]'
                                                : 'border-slate-200 dark:border-slate-700'
                                        } ${
                                            dragItem?.type === 'block' && dragItem.blockIdx === blockArrayIdx ? 'opacity-50' : ''
                                        }`}
                                        onDragOver={(e) => isEditMode && handleBlockDragOver(e, blockArrayIdx)}
                                        onDrop={(e) => isEditMode && handleBlockDrop(e, blockArrayIdx)}
                                        onDragLeave={() => {
                                            if (dragOverTarget?.type === 'block' && dragOverTarget.blockIdx === blockArrayIdx)
                                                setDragOverTarget(null);
                                        }}
                                    >
                                        {/* Block Header */}
                                        <div
                                            className={`px-5 py-4 border-b border-slate-200 dark:border-slate-700 ${
                                                isEditMode ? 'cursor-grab active:cursor-grabbing' : ''
                                            }`}
                                            draggable={isEditMode}
                                            onDragStart={(e) => isEditMode && handleBlockDragStart(e, blockArrayIdx)}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-center gap-3">
                                                    {isEditMode && (
                                                        <span className="text-slate-400 dark:text-slate-500 mr-1 cursor-grab">
                                                            <i className="fas fa-grip-vertical text-sm"></i>
                                                        </span>
                                                    )}
                                                    <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl text-white text-sm font-bold shadow-sm ${
                                                        block.blockIndex % 2 === 1 ? 'bg-primary dark:bg-primary-light' : 'bg-emerald-500 dark:bg-emerald-400'
                                                    }`}>
                                                        {block.blockIndex}
                                                    </span>
                                                    <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">Block {block.blockIndex}</h3>
                                                </div>
                                                <div className="flex items-center gap-4 text-xs">
                                                    <div className="text-center">
                                                        <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-0.5">A Zeit</div>
                                                        <div className="font-bold text-sky-600 dark:text-sky-400">{block.totalDurationA} min</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-0.5">B Zeit</div>
                                                        <div className="font-bold text-violet-600 dark:text-violet-400">{block.totalDurationB} min</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-0.5">Delta</div>
                                                        <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[11px] font-bold border ${
                                                            delta <= 3
                                                                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                                                                : delta <= 8
                                                                    ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
                                                                    : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800'
                                                        }`}>
                                                            Δ {delta} min
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Slot-based rows (pairs) */}
                                        <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
                                            {/* Venue column headers (top, like Pairings gender headers) */}
                                            <div className="grid grid-cols-2 divide-x divide-slate-200 dark:divide-slate-700">
                                                <div className="px-4 py-2 text-center bg-sky-50/50 dark:bg-sky-950/20">
                                                    <span className="text-xs font-semibold uppercase tracking-wider text-sky-600 dark:text-sky-400 flex items-center justify-center gap-1.5">
                                                        <i className="fas fa-map-marker-alt text-[10px]" aria-hidden="true"></i>
                                                        Fläche A
                                                    </span>
                                                </div>
                                                <div className="px-4 py-2 text-center bg-violet-50/40 dark:bg-violet-950/15">
                                                    <span className="text-xs font-semibold uppercase tracking-wider text-violet-600 dark:text-violet-400 flex items-center justify-center gap-1.5">
                                                        <i className="fas fa-map-marker-alt text-[10px]" aria-hidden="true"></i>
                                                        Fläche B
                                                    </span>
                                                </div>
                                            </div>
                                            {block.slots.map((slot, slotIdx) => {
                                                if (!slot.male && !slot.female) return null;

                                                const colors = getAgeGroupColors(slot.ageGroup);
                                                const isPairDragOver = dragOverTarget?.type === 'pair' && dragOverTarget.blockIdx === blockArrayIdx && dragOverTarget.slotIdx === slotIdx;
                                                const isDragSource = dragItem?.type === 'pair' && dragItem.blockIdx === blockArrayIdx && dragItem.slotIdx === slotIdx;

                                                return (
                                                    <div
                                                        key={`${block.blockIndex}-${slotIdx}`}
                                                        className={`grid grid-cols-2 divide-x divide-slate-200 dark:divide-slate-700 transition-all duration-150 ${
                                                            isDragSource ? 'opacity-40 bg-slate-50 dark:bg-slate-700/30' : ''
                                                        } ${
                                                            isPairDragOver && dragOverTarget.valid
                                                                ? 'ring-2 ring-inset ring-emerald-400 dark:ring-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/10'
                                                                : isPairDragOver && !dragOverTarget.valid
                                                                    ? 'ring-2 ring-inset ring-red-300 dark:ring-red-700 bg-red-50/30 dark:bg-red-900/10'
                                                                    : ''
                                                        } ${
                                                            isEditMode ? 'cursor-grab active:cursor-grabbing' : ''
                                                        }`}
                                                        draggable={isEditMode}
                                                        onDragStart={(e) => isEditMode && handlePairDragStart(e, blockArrayIdx, slotIdx)}
                                                        onDragOver={(e) => isEditMode && handlePairDragOver(e, blockArrayIdx, slotIdx)}
                                                        onDrop={(e) => isEditMode && handlePairDrop(e, blockArrayIdx, slotIdx)}
                                                        onDragLeave={() => {
                                                            if (isPairDragOver) setDragOverTarget(null);
                                                        }}
                                                        onDragEnd={handleDragEnd}
                                                    >
                                                        {/* Left side: whatever is on Fläche A */}
                                                        {(() => {
                                                            const aEntry = slot.maleVenue === 'Fläche A' ? slot.male : slot.female;
                                                            const aGender = slot.maleVenue === 'Fläche A'
                                                                ? (slot.male ? 'männlich' : null)
                                                                : (slot.female ? 'weiblich' : null);
                                                            return (
                                                                <div className="p-3">
                                                                    {isEditMode && (
                                                                        <div className="flex items-center gap-1 mb-1.5">
                                                                            <i className="fas fa-grip-vertical text-[10px] text-slate-300 dark:text-slate-600"></i>
                                                                            <span className={`w-2 h-2 rounded-full ${colors.bg}`}></span>
                                                                            <span className={`text-[10px] font-bold uppercase tracking-wide ${colors.text} dark:text-slate-300`}>{slot.ageGroup}</span>
                                                                        </div>
                                                                    )}
                                                                    {aEntry ? (
                                                                        <div
                                                                            className={`overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 border-l-4 bg-white dark:bg-slate-800/60 shadow-sm ${colors.borderL}`}
                                                                        >
                                                                            <div className="px-3 py-2.5">
                                                                                    <div className="flex items-center justify-between mb-1">
                                                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                                                            <span className={`text-[10px] font-bold uppercase tracking-wide ${colors.text} dark:text-slate-200`}>
                                                                                                {slot.ageGroup}
                                                                                            </span>
                                                                                            {aGender ? <GenderInline gender={aGender} small /> : <GenderInline gender="gemischt" small />}
                                                                                        </div>
                                                                                        <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">{aEntry.duration} min</span>
                                                                                    </div>
                                                                                    <div className="font-semibold text-sm text-slate-800 dark:text-slate-200 truncate">{aEntry.categoryName}</div>
                                                                                    <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-400 dark:text-slate-500">
                                                                                        <span className="flex items-center gap-1">
                                                                                            <i className="fas fa-users text-[8px]"></i>
                                                                                            {aEntry.participantCount} TN
                                                                                        </span>
                                                                                        <span className="flex items-center gap-1">
                                                                                            <i className="fas fa-clock text-[8px]"></i>
                                                                                            {aEntry.duration} min
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="text-center text-xs text-slate-400 dark:text-slate-500 italic py-4">—</div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}

                                                        {/* Right side: whatever is on Fläche B */}
                                                        {(() => {
                                                            const bEntry = slot.maleVenue === 'Fläche B' ? slot.male : slot.female;
                                                            const bGender = slot.maleVenue === 'Fläche B'
                                                                ? (slot.male ? 'männlich' : null)
                                                                : (slot.female ? 'weiblich' : null);
                                                            return (
                                                                <div className="p-3">
                                                                    {isEditMode && (
                                                                        <div className="flex items-center gap-1 mb-1.5">
                                                                            <span className={`w-2 h-2 rounded-full ${colors.bg}`}></span>
                                                                            <span className={`text-[10px] font-bold uppercase tracking-wide ${colors.text} dark:text-slate-300`}>{slot.ageGroup}</span>
                                                                        </div>
                                                                    )}
                                                                    {bEntry ? (
                                                                        <div
                                                                            className={`overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 border-l-4 bg-white dark:bg-slate-800/60 shadow-sm ${colors.borderL}`}
                                                                        >
                                                                            <div className="px-3 py-2.5">
                                                                                    <div className="flex items-center justify-between mb-1">
                                                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                                                            <span className={`text-[10px] font-bold uppercase tracking-wide ${colors.text} dark:text-slate-200`}>
                                                                                                {slot.ageGroup}
                                                                                            </span>
                                                                                            {bGender ? <GenderInline gender={bGender} small /> : <GenderInline gender="gemischt" small />}
                                                                                        </div>
                                                                                        <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">{bEntry.duration} min</span>
                                                                                    </div>
                                                                                    <div className="font-semibold text-sm text-slate-800 dark:text-slate-200 truncate">{bEntry.categoryName}</div>
                                                                                    <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-400 dark:text-slate-500">
                                                                                        <span className="flex items-center gap-1">
                                                                                            <i className="fas fa-users text-[8px]"></i>
                                                                                            {bEntry.participantCount} TN
                                                                                        </span>
                                                                                        <span className="flex items-center gap-1">
                                                                                            <i className="fas fa-clock text-[8px]"></i>
                                                                                            {bEntry.duration} min
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="text-center text-xs text-slate-400 dark:text-slate-500 italic py-4">—</div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                );
                                            })}
                                        </div>


                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {activeTab === 'sorting' && (
                    <>
                        <div className="mb-5 flex flex-col gap-4 rounded-2xl border border-slate-200/90 dark:border-slate-600/80 bg-white dark:bg-slate-800 px-4 py-4 sm:px-5 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:gap-6">
                            <div className="min-w-0">
                                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1">
                                    Dauer-Sortierung
                                </p>
                                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl">
                                    Reihenfolge der Gruppen <strong className="font-semibold text-slate-800 dark:text-slate-100">nach absteigender Kategoriedauer</strong> (längste zuerst) — getrennt nach Geschlecht.
                                    Position 1 entspricht der längsten Dauer und wird zuerst mit anderen Altersgruppen gemischt; die Liste entspricht der Logik der Block-Vorschau.
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2 shrink-0">
                                <button
                                    type="button"
                                    onClick={expandAllSortingSections}
                                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/40 px-3.5 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35"
                                >
                                    <i className="fas fa-chevron-down text-[10px] opacity-70" aria-hidden="true"></i>
                                    Alle aufklappen
                                </button>
                                <button
                                    type="button"
                                    onClick={collapseAllSortingSections}
                                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3.5 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35"
                                >
                                    <i className="fas fa-chevron-up text-[10px] opacity-70" aria-hidden="true"></i>
                                    Alle zuklappen
                                </button>
                            </div>
                        </div>

                        {sortedAgeGroupList.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800/50 px-8 py-14 text-center">
                                <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-700/50 text-slate-400 dark:text-slate-500 mb-4">
                                    <i className="fas fa-sort-amount-down text-xl" aria-hidden="true"></i>
                                </div>
                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">Keine Altersgruppen</p>
                                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                                    Es liegen keine Daten für die Dauer-Sortierung vor.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {sortedAgeGroupList.map((ag) => {
                                    const colors = getAgeGroupColors(ag.ageGroupName);
                                    const isExpanded = expandedAgeGroups[ag.ageGroupId] ?? false;
                                    const panelId = `sorting-panel-${ag.ageGroupId}`;
                                    const triggerId = `sorting-trigger-${ag.ageGroupId}`;
                                    return (
                                        <section
                                            key={ag.ageGroupId}
                                            className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm"
                                        >
                                            <button
                                                type="button"
                                                id={triggerId}
                                                aria-expanded={isExpanded}
                                                aria-controls={panelId}
                                                onClick={() => toggleAgeGroup(ag.ageGroupId)}
                                                className="flex w-full items-center gap-3 px-4 py-4 text-left transition-colors hover:bg-slate-50/90 dark:hover:bg-slate-700/25 sm:px-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/35"
                                            >
                                                <span
                                                    className={`h-2.5 w-2.5 shrink-0 rounded-full ${colors.bg}`}
                                                    aria-hidden="true"
                                                ></span>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex flex-wrap items-center gap-2 gap-y-1">
                                                        <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">
                                                            {ag.ageGroupName}
                                                        </h3>
                                                        <span className="text-xs font-medium text-slate-400 dark:text-slate-500">
                                                            {ag.minAge}–{ag.maxAge} Jahre
                                                        </span>
                                                    </div>
                                                    <div className="mt-2 flex flex-wrap gap-2">
                                                        <span className="inline-flex items-center gap-1.5 rounded-lg border border-sky-200/80 dark:border-sky-800/50 bg-sky-50/70 dark:bg-sky-950/25 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">
                                                            <i className="fas fa-mars text-[9px]" aria-hidden="true"></i>
                                                            M {ag.male.length}
                                                        </span>
                                                        <span className="inline-flex items-center gap-1.5 rounded-lg border border-pink-200/80 dark:border-pink-800/50 bg-pink-50/70 dark:bg-pink-950/25 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-pink-700 dark:text-pink-300">
                                                            <i className="fas fa-venus text-[9px]" aria-hidden="true"></i>
                                                            W {ag.female.length}
                                                        </span>
                                                    </div>
                                                </div>
                                                <span
                                                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200/90 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/40 text-slate-500 dark:text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                                                    aria-hidden="true"
                                                >
                                                    <i className="fas fa-chevron-down text-sm"></i>
                                                </span>
                                            </button>

                                            {isExpanded && (
                                                <div
                                                    id={panelId}
                                                    role="region"
                                                    aria-labelledby={triggerId}
                                                    className="border-t border-slate-200 dark:border-slate-700"
                                                >
                                                    <p className="px-4 py-2.5 text-[11px] leading-snug text-slate-500 dark:text-slate-400 sm:px-5 bg-slate-50/50 dark:bg-slate-900/30 border-b border-slate-100 dark:border-slate-700/80">
                                                        <strong className="font-semibold text-slate-600 dark:text-slate-300">Legende:</strong>{' '}
                                                        Die Rangzahl ist die Sortierposition innerhalb des Geschlechts (1 = längste Kategoriedauer zuerst).
                                                    </p>
                                                    <div className="grid grid-cols-1 divide-y divide-slate-200 dark:divide-slate-700 lg:grid-cols-2 lg:divide-y-0 lg:divide-x">
                                                        {[
                                                            {
                                                                key: 'male',
                                                                entries: ag.male,
                                                                icon: 'fa-mars',
                                                                label: 'Männlich',
                                                                headBg: 'bg-sky-50/55 dark:bg-sky-950/20',
                                                                headText: 'text-sky-600 dark:text-sky-400',
                                                            },
                                                            {
                                                                key: 'female',
                                                                entries: ag.female,
                                                                icon: 'fa-venus',
                                                                label: 'Weiblich',
                                                                headBg: 'bg-pink-50/55 dark:bg-pink-950/20',
                                                                headText: 'text-pink-600 dark:text-pink-400',
                                                            },
                                                        ].map((col) => (
                                                            <div key={col.key} className="flex min-h-0 flex-col">
                                                                <div
                                                                    className={`border-b border-slate-200 dark:border-slate-700 px-4 py-2.5 sm:px-5 ${col.headBg}`}
                                                                >
                                                                    <span
                                                                        className={`flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest ${col.headText}`}
                                                                    >
                                                                        <i className={`fas ${col.icon} text-[10px]`} aria-hidden="true"></i>
                                                                        {col.label}{' '}
                                                                        <span className="font-bold opacity-90">({col.entries.length})</span>
                                                                    </span>
                                                                </div>
                                                                <div className="min-h-[4.5rem] divide-y divide-slate-100 dark:divide-slate-700/60">
                                                                    {col.entries.length === 0 ? (
                                                                        <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
                                                                            <i
                                                                                className="fas fa-inbox text-2xl text-slate-300 dark:text-slate-600"
                                                                                aria-hidden="true"
                                                                            ></i>
                                                                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                                                                Keine Gruppen
                                                                            </p>
                                                                        </div>
                                                                    ) : (
                                                                        col.entries.map((entry, idx) => (
                                                                            <div
                                                                                key={idx}
                                                                                className="flex flex-col gap-2 px-4 py-3.5 transition-colors hover:bg-slate-50/80 dark:hover:bg-slate-800/50 sm:flex-row sm:items-center sm:justify-between sm:px-5"
                                                                            >
                                                                                <div className="flex min-w-0 items-start gap-3">
                                                                                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[11px] font-bold text-slate-500 shadow-sm dark:bg-slate-700/70 dark:text-slate-300">
                                                                                        {idx + 1}
                                                                                    </span>
                                                                                    <div className="min-w-0 pt-0.5">
                                                                                        <p className="font-semibold text-sm leading-snug text-slate-800 dark:text-slate-100 break-words">
                                                                                            {entry.groupName || entry.categoryName}
                                                                                        </p>
                                                                                        {entry.categoryType ? (
                                                                                            <p className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
                                                                                                {entry.categoryType}
                                                                                            </p>
                                                                                        ) : null}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="flex flex-wrap items-center gap-2 pl-11 sm:shrink-0 sm:justify-end sm:pl-0 sm:gap-3">
                                                                                    <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                                                                                        <i className="fas fa-users w-3.5 text-center text-[9px] opacity-75" aria-hidden="true"></i>
                                                                                        {entry.participantCount} TN
                                                                                    </span>
                                                                                    <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200/90 dark:border-slate-600 bg-white/80 px-2 py-1 text-xs font-semibold text-slate-600 shadow-sm dark:bg-slate-800/60 dark:text-slate-300">
                                                                                        <i className="fas fa-clock text-[9px] opacity-70" aria-hidden="true"></i>
                                                                                        {entry.duration} min
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </section>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'pairings' && (
                    Object.keys(blockPairings).length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800/50 px-8 py-14 text-center">
                            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-700/50 text-slate-400 dark:text-slate-500 mb-4">
                                <i className="fas fa-link text-xl" aria-hidden="true"></i>
                            </div>
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">Keine Pairings</p>
                            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                                Für die aktuelle Konfiguration liegen keine Pairing-Daten vor. Nach einem erfolgreichen Lauf der Zeitplanlogik erscheinen sie hier nach Block gruppiert.
                            </p>
                        </div>
                    ) : (
                    <>
                        <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-slate-200/90 dark:border-slate-600/80 bg-white dark:bg-slate-800 px-4 py-4 sm:px-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6 shadow-sm">
                            <div className="min-w-0">
                                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1">Pairings</p>
                                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                    Zugeordnete M/W-Paare pro Block mit Dauerabweichung (Delta) zwischen den beiden Gruppen.
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2 sm:justify-end shrink-0" aria-label="Delta-Legende: grün gut, gelb mittel, rot stark">
                                <span className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50/80 dark:bg-emerald-900/20 px-2.5 py-1 text-[10px] font-semibold text-emerald-800 dark:text-emerald-200">
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true"></span>
                                    ≤3 min
                                </span>
                                <span className="inline-flex items-center gap-1.5 rounded-xl border border-amber-200/80 dark:border-amber-800/60 bg-amber-50/80 dark:bg-amber-900/20 px-2.5 py-1 text-[10px] font-semibold text-amber-900 dark:text-amber-200">
                                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true"></span>
                                    4–8 min
                                </span>
                                <span className="inline-flex items-center gap-1.5 rounded-xl border border-red-200/80 dark:border-red-800/60 bg-red-50/80 dark:bg-red-900/20 px-2.5 py-1 text-[10px] font-semibold text-red-800 dark:text-red-200">
                                    <span className="h-1.5 w-1.5 rounded-full bg-red-500" aria-hidden="true"></span>
                                    &gt;8 min
                                </span>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                            {[...Object.entries(blockPairings)]
                                .sort((a, b) => parseInt(a[0], 10) - parseInt(b[0], 10))
                                .map(([blockNum, pairings]) => {
                                    const n = pairings.length;
                                    return (
                            <article
                                key={blockNum}
                                className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm"
                            >
                                <header className="flex flex-col gap-3 border-b border-slate-200 dark:border-slate-700 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <span
                                            className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white shadow-sm ${
                                                parseInt(blockNum, 10) % 2 === 1
                                                    ? 'bg-primary dark:bg-primary-light'
                                                    : 'bg-emerald-500 dark:bg-emerald-400'
                                            }`}
                                        >
                                            {blockNum}
                                        </span>
                                        <div className="min-w-0">
                                            <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 truncate">
                                                Block {blockNum}
                                            </h3>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                                {n} {n === 1 ? 'Paar' : 'Paare'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400 sm:text-right">
                                        <i className="fas fa-link text-slate-400 dark:text-slate-500 text-[10px]" aria-hidden="true"></i>
                                        <span>Männlich · Weiblich · Delta</span>
                                    </div>
                                </header>

                                {/* Tabellenkopf — ab sm sichtbar als Spalten; mobil als erklärender Fließtext oben genügend */}
                                <div
                                    className="hidden sm:grid sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_5.25rem] sm:divide-x sm:divide-slate-200 dark:sm:divide-slate-700 sm:border-b sm:border-slate-200 dark:sm:border-slate-700"
                                    role="row"
                                >
                                    <div className="bg-sky-50/50 dark:bg-sky-950/20 px-4 py-2.5" role="columnheader">
                                        <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-sky-600 dark:text-sky-400">
                                            <i className="fas fa-mars text-[10px]" aria-hidden="true"></i>
                                            Männlich
                                        </span>
                                    </div>
                                    <div className="bg-pink-50/50 dark:bg-pink-950/20 px-4 py-2.5" role="columnheader">
                                        <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-pink-600 dark:text-pink-400">
                                            <i className="fas fa-venus text-[10px]" aria-hidden="true"></i>
                                            Weiblich
                                        </span>
                                    </div>
                                    <div
                                        className="flex items-center justify-center bg-slate-50/90 dark:bg-slate-800/60 px-2 py-2.5"
                                        role="columnheader"
                                    >
                                        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                                            Delta
                                        </span>
                                    </div>
                                </div>

                                <div className="divide-y divide-slate-100 dark:divide-slate-700/60" role="list">
                                    {pairings.map((p, idx) => {
                                        const colors = getAgeGroupColors(p.ageGroup);
                                        const diff = p.durationDiff;
                                        return (
                                            <div
                                                key={`${blockNum}-${idx}`}
                                                className="group transition-colors hover:bg-slate-50/60 dark:hover:bg-slate-800/40"
                                                role="listitem"
                                            >
                                                <div className="flex items-center gap-2 border-b border-slate-100/90 dark:border-slate-700/50 bg-slate-50/40 dark:bg-slate-900/25 px-4 py-2 sm:px-5">
                                                    <span
                                                        className={`h-2 w-2 shrink-0 rounded-full ${colors.bg}`}
                                                        aria-hidden="true"
                                                    ></span>
                                                    <span
                                                        className={`text-[11px] font-bold uppercase tracking-wide ${colors.text} dark:text-slate-200`}
                                                    >
                                                        {p.ageGroup}
                                                    </span>
                                                    <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
                                                        · Paar {idx + 1}
                                                    </span>
                                                </div>

                                                <div className="grid grid-cols-1 min-[480px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_5.25rem] min-[480px]:divide-x min-[480px]:divide-slate-200 dark:min-[480px]:divide-slate-700">
                                                    <div className="border-b border-slate-100 dark:border-slate-700/70 px-4 py-3.5 min-[480px]:border-b-0 bg-sky-50/25 dark:bg-sky-950/10 min-[480px]:bg-sky-50/20 dark:min-[480px]:bg-sky-950/10">
                                                        <div className="mb-1.5 flex items-center gap-1.5 min-[480px]:hidden text-[10px] font-semibold uppercase tracking-widest text-sky-600 dark:text-sky-400">
                                                            <i className="fas fa-mars text-[10px]" aria-hidden="true"></i>
                                                            Männlich
                                                        </div>
                                                        {renderPairingSideCell(p.male)}
                                                    </div>
                                                    <div className="border-b border-slate-100 dark:border-slate-700/70 px-4 py-3.5 min-[480px]:border-b-0 bg-pink-50/25 dark:bg-pink-950/10 min-[480px]:bg-pink-50/20 dark:min-[480px]:bg-pink-950/10">
                                                        <div className="mb-1.5 flex items-center gap-1.5 min-[480px]:hidden text-[10px] font-semibold uppercase tracking-widest text-pink-600 dark:text-pink-400">
                                                            <i className="fas fa-venus text-[10px]" aria-hidden="true"></i>
                                                            Weiblich
                                                        </div>
                                                        {renderPairingSideCell(p.female)}
                                                    </div>
                                                    <div className="flex flex-col justify-center gap-1 px-4 py-3 min-[480px]:px-2 min-[480px]:py-3 bg-slate-50/40 dark:bg-slate-800/30">
                                                        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 min-[480px]:sr-only">
                                                            Delta
                                                        </div>
                                                        <div className="flex min-[480px]:justify-center">
                                                            {diff !== null && diff !== undefined ? (
                                                                <span
                                                                    className={`inline-flex items-center gap-0.5 rounded-full border px-2.5 py-1 text-[11px] font-bold whitespace-nowrap ${deltaPairingClass(diff)}`}
                                                                    title="Absolute Differenz der Kategoriedauern (min)"
                                                                >
                                                                    ±{diff} min
                                                                </span>
                                                            ) : (
                                                                <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </article>
                                    );
                                })}
                        </div>
                    </>
                    )
                )}

                {activeTab === 'partners' && (
                    <>
                        <div className="mb-5 flex flex-col gap-4 rounded-2xl border border-slate-200/90 dark:border-slate-600/80 bg-white dark:bg-slate-800 px-4 py-4 sm:px-5 shadow-sm lg:flex-row lg:items-start lg:justify-between lg:gap-8">
                            <div className="min-w-0 flex-1">
                                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1">
                                    Partnerformen
                                </p>
                                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                    Diese Gruppen laufen als <strong className="font-semibold text-slate-800 dark:text-slate-100">eigener Block</strong> am Ende des Ablaufs, auf{' '}
                                    <strong className="font-semibold text-slate-800 dark:text-slate-100">beide Flächen</strong> verteilt. Die angezeigte Dauer richtet sich nach der
                                    Paarzahl (Teilnehmer ÷ 2), nicht nach Einzelathleten-Minuten.
                                </p>
                            </div>
                            {partnerFormsSummary.count > 0 ? (
                                <div className="flex flex-wrap gap-2 shrink-0 lg:pt-0.5">
                                    <span className="inline-flex items-center gap-1.5 rounded-xl border border-purple-200/80 dark:border-purple-800/50 bg-purple-50/80 dark:bg-purple-950/30 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-purple-800 dark:text-purple-200">
                                        <i className="fas fa-users text-[9px] opacity-80" aria-hidden="true"></i>
                                        {partnerFormsSummary.count} {partnerFormsSummary.count === 1 ? 'Gruppe' : 'Gruppen'}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/40 px-2.5 py-1 text-[10px] font-semibold text-slate-700 dark:text-slate-200">
                                        <i className="fas fa-user-friends text-[9px] opacity-70" aria-hidden="true"></i>
                                        {partnerFormsSummary.totalTn} TN
                                    </span>
                                    <span className="inline-flex items-center gap-1.5 rounded-xl border border-violet-200/80 dark:border-violet-800/50 bg-violet-50/70 dark:bg-violet-950/25 px-2.5 py-1 text-[10px] font-semibold text-violet-800 dark:text-violet-200">
                                        <i className="fas fa-link text-[9px] opacity-70" aria-hidden="true"></i>
                                        {partnerFormsSummary.totalPairs} Paare
                                    </span>
                                </div>
                            ) : null}
                        </div>

                        {!data.partnerForms || data.partnerForms.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800/50 px-8 py-14 text-center">
                                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-50 dark:bg-purple-950/30 text-purple-400 dark:text-purple-500">
                                    <i className="fas fa-handshake text-xl" aria-hidden="true"></i>
                                </div>
                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">Keine Partnerformen</p>
                                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto leading-relaxed">
                                    Es sind keine Partnerform-Gruppen für die Vorschau hinterlegt. Sobald welche existieren, erscheinen sie hier mit Kategorie, Altersgruppe und
                                    Dauer.
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
                                <div
                                    className="hidden md:grid md:grid-cols-12 md:gap-4 md:border-b md:border-slate-200 md:bg-slate-50/90 md:px-5 md:py-2.5 dark:md:border-slate-700 dark:md:bg-slate-900/40"
                                    role="row"
                                >
                                    <div className="md:col-span-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400" role="columnheader">
                                        #
                                    </div>
                                    <div className="md:col-span-4 text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400" role="columnheader">
                                        Gruppe / Kategorie
                                    </div>
                                    <div className="md:col-span-3 text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400" role="columnheader">
                                        Altersgruppe
                                    </div>
                                    <div
                                        className="md:col-span-4 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400"
                                        role="columnheader"
                                    >
                                        Kennzahlen
                                    </div>
                                </div>
                                <ul className="divide-y divide-slate-100 dark:divide-slate-700/60" role="list">
                                    {data.partnerForms.map((pf, idx) => {
                                        const colors = getAgeGroupColors(pf.ageGroup);
                                        const pairs = pf.pairCount ?? Math.ceil((pf.participantCount || 0) / 2);
                                        return (
                                            <li
                                                key={`${pf.groupName || 'g'}-${idx}`}
                                                className="transition-colors hover:bg-slate-50/85 dark:hover:bg-slate-800/55"
                                                aria-label={`Partnerform ${idx + 1}: ${pf.groupName}`}
                                            >
                                                <div className="flex flex-col gap-3 px-4 py-4 sm:px-5 md:grid md:grid-cols-12 md:items-center md:gap-4 md:py-3.5">
                                                    <div className="flex items-center md:col-span-1">
                                                        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[11px] font-bold text-slate-500 shadow-sm dark:bg-slate-700/70 dark:text-slate-300">
                                                            {idx + 1}
                                                        </span>
                                                    </div>
                                                    <div className="min-w-0 md:col-span-4">
                                                        <p className="font-semibold text-sm leading-snug text-slate-900 dark:text-slate-100 break-words">
                                                            {pf.groupName}
                                                        </p>
                                                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{pf.categoryName}</p>
                                                    </div>
                                                    <div className="md:col-span-3">
                                                        <div className="inline-flex max-w-full items-center gap-2 rounded-lg border border-slate-200/80 bg-slate-50/80 px-2.5 py-1 dark:border-slate-600/60 dark:bg-slate-800/50">
                                                            <span
                                                                className={`h-2 w-2 shrink-0 rounded-full ${colors.bg}`}
                                                                aria-hidden="true"
                                                            ></span>
                                                            <span className={`text-xs font-semibold ${colors.text} dark:text-slate-200 truncate`}>
                                                                {pf.ageGroup}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-2 md:col-span-4 md:justify-end">
                                                        <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200/90 bg-white px-2 py-1 text-xs font-medium text-slate-600 shadow-sm dark:border-slate-600 dark:bg-slate-800/60 dark:text-slate-300">
                                                            <i className="fas fa-users w-3.5 text-center text-[9px] text-slate-400 dark:text-slate-500" aria-hidden="true"></i>
                                                            {pf.participantCount} TN
                                                        </span>
                                                        <span
                                                            className="inline-flex items-center gap-1.5 rounded-lg border border-purple-200/90 bg-purple-50/90 px-2 py-1 text-xs font-semibold text-purple-800 shadow-sm dark:border-purple-800/50 dark:bg-purple-950/40 dark:text-purple-200"
                                                            title="Anzahl Paare für die Dauerberechnung"
                                                        >
                                                            <i className="fas fa-link w-3.5 text-center text-[9px] opacity-80" aria-hidden="true"></i>
                                                            {pairs} Paare
                                                        </span>
                                                        <DurationBadge minutes={pf.duration} />
                                                    </div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'overview' && (
                    <>
                        <div className="mb-5 flex flex-col gap-4 rounded-2xl border border-slate-200/90 dark:border-slate-600/80 bg-white dark:bg-slate-800 px-4 py-4 sm:px-5 shadow-sm lg:flex-row lg:items-start lg:justify-between lg:gap-8">
                            <div className="min-w-0 flex-1">
                                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1">
                                    Flächen-Zuweisung
                                </p>
                                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                    Pro Altersgruppe ist festgelegt, ob die <strong className="font-semibold text-slate-800 dark:text-slate-100">männlichen</strong> und{' '}
                                    <strong className="font-semibold text-slate-800 dark:text-slate-100">weiblichen</strong> Gruppen auf{' '}
                                    <strong className="font-semibold text-slate-800 dark:text-slate-100">Fläche A</strong> oder{' '}
                                    <strong className="font-semibold text-slate-800 dark:text-slate-100">Fläche B</strong> laufen —{' '}
                                    <span className="text-slate-500 dark:text-slate-400">gilt für alle Blöcke gleich.</span>
                                </p>
                                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                                    Der <strong className="font-medium text-slate-600 dark:text-slate-300">Index</strong> beschreibt die Reihenfolge der Altersgruppe; gerade/ungerade
                                    wird in der Rotationslogik genutzt.
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2 shrink-0 lg:pt-0.5" aria-label="Flächen-Legende">
                                <span className="inline-flex items-center gap-1.5 rounded-xl border border-sky-200/90 dark:border-sky-800/60 bg-sky-50/90 dark:bg-sky-950/25 px-2.5 py-1 text-[10px] font-semibold text-sky-800 dark:text-sky-200">
                                    <i className="fas fa-map-marker-alt text-[9px] opacity-80" aria-hidden="true"></i>
                                    Fläche A
                                </span>
                                <span className="inline-flex items-center gap-1.5 rounded-xl border border-violet-200/90 dark:border-violet-800/60 bg-violet-50/90 dark:bg-violet-950/25 px-2.5 py-1 text-[10px] font-semibold text-violet-800 dark:text-violet-200">
                                    <i className="fas fa-map-marker-alt text-[9px] opacity-80" aria-hidden="true"></i>
                                    Fläche B
                                </span>
                            </div>
                        </div>

                        {!data.ageGroupOrder || data.ageGroupOrder.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800/50 px-8 py-14 text-center">
                                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light">
                                    <i className="fas fa-map-marker-alt text-xl" aria-hidden="true"></i>
                                </div>
                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">Keine Flächen-Daten</p>
                                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto leading-relaxed">
                                    Es liegen keine Einträge für die Altersgruppen-Reihenfolge und Flächenzuweisung vor.
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
                                <div
                                    className="hidden md:grid md:grid-cols-12 md:gap-4 md:border-b md:border-slate-200 md:bg-slate-50/90 md:px-5 md:py-2.5 dark:md:border-slate-700 dark:md:bg-slate-900/40"
                                    role="row"
                                >
                                    <div className="md:col-span-4 text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400" role="columnheader">
                                        Altersgruppe
                                    </div>
                                    <div className="md:col-span-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400" role="columnheader">
                                        Index
                                    </div>
                                    <div
                                        className="md:col-span-3 text-center text-[10px] font-semibold uppercase tracking-widest text-sky-600 dark:text-sky-400"
                                        role="columnheader"
                                    >
                                        <i className="fas fa-mars mr-1 text-[10px]" aria-hidden="true"></i>
                                        Männlich
                                    </div>
                                    <div
                                        className="md:col-span-3 text-center text-[10px] font-semibold uppercase tracking-widest text-pink-600 dark:text-pink-400"
                                        role="columnheader"
                                    >
                                        <i className="fas fa-venus mr-1 text-[10px]" aria-hidden="true"></i>
                                        Weiblich
                                    </div>
                                </div>
                                <ul className="divide-y divide-slate-100 dark:divide-slate-700/60" role="list">
                                    {data.ageGroupOrder.map((ag) => {
                                        const colors = getAgeGroupColors(ag.name);
                                        const isEven = ag.index % 2 === 0;
                                        return (
                                            <li
                                                key={ag.index}
                                                className="transition-colors hover:bg-slate-50/85 dark:hover:bg-slate-800/55"
                                                aria-label={`${ag.name}, Index ${ag.index}`}
                                            >
                                                <div className="flex flex-col gap-4 px-4 py-4 sm:px-5 md:grid md:grid-cols-12 md:items-center md:gap-4 md:py-3.5">
                                                    <div className="flex min-w-0 items-center gap-3 md:col-span-4">
                                                        <span
                                                            className={`h-2.5 w-2.5 shrink-0 rounded-full ${colors.bg}`}
                                                            aria-hidden="true"
                                                        ></span>
                                                        <span className={`text-sm font-semibold leading-snug ${colors.text} dark:text-slate-100 break-words`}>
                                                            {ag.name}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-2 md:col-span-2">
                                                        <span className="inline-flex h-8 min-w-[2rem] items-center justify-center rounded-lg bg-slate-100 px-2 text-xs font-bold text-slate-600 dark:bg-slate-700/70 dark:text-slate-300">
                                                            {ag.index}
                                                        </span>
                                                        <span
                                                            className={`inline-flex items-center rounded-lg border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                                                                isEven
                                                                    ? 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-600 dark:bg-slate-800/60 dark:text-slate-300'
                                                                    : 'border-slate-300/80 bg-white text-slate-700 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-200'
                                                            }`}
                                                        >
                                                            {isEven ? 'gerade' : 'ungerade'}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-col gap-1.5 md:col-span-3 md:items-center">
                                                        <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-sky-600 dark:text-sky-400 md:hidden">
                                                            <i className="fas fa-mars text-[10px]" aria-hidden="true"></i>
                                                            Männlich
                                                        </span>
                                                        <span
                                                            className={`inline-flex w-full items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold shadow-sm md:w-auto md:min-w-[7.5rem] ${venueSurfaceBadgeClass(ag.maleVenue)}`}
                                                        >
                                                            <i className="fas fa-map-marker-alt text-[10px] opacity-85" aria-hidden="true"></i>
                                                            {ag.maleVenue}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-col gap-1.5 md:col-span-3 md:items-center">
                                                        <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-pink-600 dark:text-pink-400 md:hidden">
                                                            <i className="fas fa-venus text-[10px]" aria-hidden="true"></i>
                                                            Weiblich
                                                        </span>
                                                        <span
                                                            className={`inline-flex w-full items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold shadow-sm md:w-auto md:min-w-[7.5rem] ${venueSurfaceBadgeClass(ag.femaleVenue)}`}
                                                        >
                                                            <i className="fas fa-map-marker-alt text-[10px] opacity-85" aria-hidden="true"></i>
                                                            {ag.femaleVenue}
                                                        </span>
                                                    </div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </>
                )}
            </div>
        </main>
    );
};

export default ScheduleBlockPreview;
